<template>
  <div class="premium-chart-wrap">
    <div class="chart-header">
      <div class="chart-title">溢价率走势</div>
      <div class="range-tabs">
        <span
          v-for="opt in rangeOptions"
          :key="opt.key"
          :class="['range-tab', { active: selectedRange === opt.key }]"
          @click="selectRange(opt.key)"
        >{{ opt.label }}</span>
      </div>
    </div>

    <div v-if="rangeStats.count" class="range-stats">
      <div class="range-stat-item">
        <span class="range-stat-label">均值</span>
        <span :class="premiumClass(rangeStats.mean)">{{ formatPremium(rangeStats.mean) }}</span>
      </div>
      <div class="range-stat-item">
        <span class="range-stat-label">区间</span>
        <span>{{ formatPremium(rangeStats.min) }} ~ {{ formatPremium(rangeStats.max) }}</span>
      </div>
      <div class="range-stat-item">
        <span class="range-stat-label">当前</span>
        <span :class="premiumClass(rangeStats.current)">{{ formatPremium(rangeStats.current) }}</span>
      </div>
      <div class="range-stat-item">
        <span class="range-stat-label">样本</span>
        <span>{{ rangeStats.count }} 天</span>
      </div>
    </div>
    <div v-else class="range-stats-empty">该区间暂无溢价数据</div>

    <div ref="chartEl" class="premium-chart" />
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([LineChart, GridComponent, TooltipComponent, MarkLineComponent, CanvasRenderer])

const RANGE_OPTIONS = [
  { key: 'week', label: '一周', days: 7 },
  { key: 'month', label: '一月', days: 30 },
  { key: 'quarter', label: '一季度', days: 90 },
  { key: 'half', label: '半年', days: 180 },
  { key: 'year', label: '一年', days: 365 },
  { key: 'all', label: '全部', days: null }
]

export default {
  name: 'HistoryPremiumChart',
  props: {
    records: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null,
      selectedRange: 'month',
      rangeOptions: RANGE_OPTIONS
    }
  },
  computed: {
    filteredRecords() {
      return this.filterByRange(this.records, this.selectedRange)
    },
    rangeStats() {
      return this.calcStats(this.filteredRecords)
    }
  },
  watch: {
    records: {
      deep: true,
      handler() {
        this.renderChart()
      }
    },
    selectedRange() {
      this.renderChart()
    }
  },
  mounted() {
    this.renderChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeChart()
  },
  methods: {
    selectRange(key) {
      this.selectedRange = key
    },
    getRangeDays(key) {
      const opt = RANGE_OPTIONS.find((item) => item.key === key)
      return opt ? opt.days : null
    },
    filterByRange(records, rangeKey) {
      const days = this.getRangeDays(rangeKey)
      if (!days || !Array.isArray(records) || !records.length) {
        return Array.isArray(records) ? records.slice() : []
      }
      const cutoff = new Date()
      cutoff.setHours(0, 0, 0, 0)
      cutoff.setDate(cutoff.getDate() - days)
      const cutoffStr = this.formatDate(cutoff)
      return records.filter((row) => {
        const dt = String(row.price_dt || '').slice(0, 10)
        return dt && dt >= cutoffStr
      })
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = `${date.getMonth() + 1}`.padStart(2, '0')
      const d = `${date.getDate()}`.padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    calcStats(records) {
      const premiums = []
      let current = null
      const sorted = [...(records || [])].sort((a, b) => {
        return String(a.price_dt || '').localeCompare(String(b.price_dt || ''))
      })
      sorted.forEach((row) => {
        const premium = this.parsePremium(row.discount_rt)
        if (premium === null) {
          return
        }
        premiums.push(premium)
        current = premium
      })
      if (!premiums.length) {
        return { count: 0 }
      }
      let sum = 0
      let min = premiums[0]
      let max = premiums[0]
      premiums.forEach((p) => {
        sum += p
        min = Math.min(min, p)
        max = Math.max(max, p)
      })
      const mean = sum / premiums.length
      return {
        count: premiums.length,
        mean: this.round2(mean),
        min: this.round2(min),
        max: this.round2(max),
        current: current !== null ? this.round2(current) : null
      }
    },
    round2(val) {
      return Math.round(val * 100) / 100
    },
    formatPremium(val) {
      if (val === null || val === undefined || val === '' || val === '-') {
        return '--'
      }
      const num = Number(val)
      if (!Number.isFinite(num)) {
        return String(val)
      }
      return `${num}%`
    },
    premiumClass(val) {
      const num = Number(val)
      if (!Number.isFinite(num)) {
        return ''
      }
      return num < 0 ? 'premium-negative' : 'premium-positive'
    },
    resize() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    handleResize() {
      this.resize()
    },
    parsePremium(val) {
      if (val === null || val === undefined || val === '' || val === '-') {
        return null
      }
      const num = Number(val)
      return Number.isFinite(num) ? num : null
    },
    buildSeriesData() {
      const sorted = [...this.filteredRecords].sort((a, b) => {
        return String(a.price_dt || '').localeCompare(String(b.price_dt || ''))
      })
      const dates = []
      const values = []
      sorted.forEach((row) => {
        const premium = this.parsePremium(row.discount_rt)
        if (premium === null) {
          return
        }
        dates.push(row.price_dt)
        values.push(premium)
      })
      return { dates, values }
    },
    renderChart() {
      if (!this.$refs.chartEl) {
        return
      }
      const { dates, values } = this.buildSeriesData()
      if (!dates.length) {
        this.disposeChart()
        return
      }
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chartEl)
      }
      this.chart.setOption({
        textStyle: {
          fontFamily: '-apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif'
        },
        animation: true,
        grid: {
          left: 48,
          right: 16,
          top: 24,
          bottom: 48
        },
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const item = params && params[0]
            if (!item) {
              return ''
            }
            const val = item.data
            const sign = val >= 0 ? '+' : ''
            return `${item.axisValue}<br/>溢价率：${sign}${val}%`
          }
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLabel: {
            fontSize: 10,
            rotate: dates.length > 14 ? 35 : 0
          },
          axisLine: {
            lineStyle: { color: '#ddd' }
          }
        },
        yAxis: {
          type: 'value',
          name: '%',
          nameTextStyle: { fontSize: 11, color: '#999' },
          axisLabel: {
            fontSize: 10,
            formatter: '{value}%'
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' }
          }
        },
        series: [
          {
            name: '溢价率',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: dates.length > 30 ? 4 : 6,
            lineStyle: {
              width: 2,
              color: '#ff6f2f'
            },
            itemStyle: {
              color: '#ff6f2f'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(255, 111, 47, 0.25)' },
                  { offset: 1, color: 'rgba(255, 111, 47, 0.02)' }
                ]
              }
            },
            data: values,
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: {
                color: '#999',
                type: 'dashed'
              },
              label: {
                formatter: '0%',
                fontSize: 10
              },
              data: [{ yAxis: 0 }]
            }
          }
        ]
      }, true)
    }
  }
}
</script>

<style scoped>
.premium-chart-wrap {
  margin-top: 12px;
  padding: 10px 8px 4px;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
}

.chart-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  padding: 0 4px;
}

.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.range-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.range-tab {
  padding: 2px 8px;
  font-size: 11px;
  color: #666;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
}

.range-tab.active {
  color: #fff;
  background: #ff6f2f;
  border-color: #ff6f2f;
}

.range-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 6px;
  padding: 8px 10px;
  background: #fafafa;
  border-radius: 4px;
}

.range-stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
}

.range-stat-label {
  color: #999;
}

.range-stats-empty {
  margin-bottom: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #999;
  background: #fafafa;
  border-radius: 4px;
}

.premium-positive {
  color: #e74c3c;
}

.premium-negative {
  color: #27ae60;
}

.premium-chart {
  width: 100%;
  height: 240px;
}
</style>
